/**
 * The small map picture at the top of the ride receipt: a Google Static Maps
 * image with a green pickup pin and a red drop pin. It is one cached image
 * request, not a live map, and it draws no route line (the ride does not store a
 * traced route, so only the two endpoints are shown).
 *
 * Pure functions so the zoom/centre maths can be unit-tested: Static Maps takes a
 * whole-number zoom and a centre, so we choose them ourselves to keep both pins,
 * including the pin's head above its tip, inside the picture.
 */

const TILE = 256; // Web Mercator world size at zoom 0
const MIN_ZOOM = 0;
const MAX_ZOOM = 17;
const MAX_IMAGE_PX = 640; // Static Maps limit per side (before scale)
const RAD = Math.PI / 180;

// A pin's tip is its position; its head rises ~40px above it.
export const DEFAULT_PADDING = { top: 52, right: 40, bottom: 20, left: 40 };

export const PICKUP_PIN_COLOR = '0x008957';
export const DROP_PIN_COLOR = '0xef4444';

export const validPoint = (p) => {
  if (!p || p.lat == null || p.lng == null || p.lat === '' || p.lng === '') return false;
  if (![p.lat, p.lng].every((value) => typeof value === 'number' || (typeof value === 'string' && value.trim() !== ''))) return false;
  const lat = Number(p.lat);
  const lng = Number(p.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 85 && Math.abs(lng) <= 180;
};

const project = (p) => {
  const s = Math.sin(Number(p.lat) * RAD);
  return {
    x: ((Number(p.lng) + 180) / 360) * TILE,
    y: (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * TILE,
  };
};

const unproject = ({ x, y }) => ({
  lat: (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / TILE))) / RAD),
  lng: (x / TILE) * 360 - 180,
});

/**
 * Centre and whole-number zoom that fit both points inside a `width` x `height`
 * picture, leaving `padding` free on each side. Zoom is clamped to 0..17, so very
 * close points do not zoom into a blur and very far ones still render.
 * Returns null if either point is invalid.
 */
export const staticMapView = (pickup, destination, { width, height, padding = DEFAULT_PADDING }) => {
  if (!validPoint(pickup) || !validPoint(destination)) return null;
  const a = project(pickup);
  const b = project(destination);
  // Choose the shorter span when endpoints straddle the date line.
  if (b.x - a.x > TILE / 2) b.x -= TILE;
  else if (a.x - b.x > TILE / 2) b.x += TILE;
  const dx = Math.abs(a.x - b.x);
  const dy = Math.abs(a.y - b.y);
  const availW = Math.max(1, width - padding.left - padding.right);
  const availH = Math.max(1, height - padding.top - padding.bottom);

  const fitX = dx > 0 ? Math.log2(availW / dx) : Infinity;
  const fitY = dy > 0 ? Math.log2(availH / dy) : Infinity;
  const zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.floor(Math.min(fitX, fitY))));

  // The padded content box is centred in the frame, so the centre sits half the
  // padding difference away from the midpoint (north, since the pin heads need room).
  const unitsPerPx = 1 / 2 ** zoom;
  const center = unproject({
    x: (a.x + b.x) / 2 + ((padding.right - padding.left) / 2) * unitsPerPx,
    y: (a.y + b.y) / 2 + ((padding.bottom - padding.top) / 2) * unitsPerPx,
  });
  center.lng = ((center.lng + 180) % 360 + 360) % 360 - 180;
  return { center, zoom };
};

const coord = (n) => Number(n).toFixed(5);

/**
 * The Static Maps image URL for a ride, or null when it cannot be drawn (no API
 * key, or a missing/invalid point). `width`/`height` are CSS pixels; the image is
 * requested at `scale` x for sharp rendering on phone screens.
 */
export const buildStaticMapUrl = ({ pickup, destination, key, width, height, scale = 2 }) => {
  if (!key) return null;
  const w = Math.min(MAX_IMAGE_PX, Math.round(Number(width)));
  const h = Math.min(MAX_IMAGE_PX, Math.round(Number(height)));
  if (!Number.isFinite(w) || !Number.isFinite(h) || w < 100 || h < 60) return null;
  const view = staticMapView(pickup, destination, { width: w, height: h });
  if (!view) return null;

  const pin = (color, p) => `markers=color:${color}%7Csize:mid%7C${coord(p.lat)},${coord(p.lng)}`;
  return `https://maps.googleapis.com/maps/api/staticmap?${[
    `center=${coord(view.center.lat)},${coord(view.center.lng)}`,
    `zoom=${view.zoom}`,
    `size=${w}x${h}`,
    `scale=${scale === 1 ? 1 : 2}`,
    'maptype=roadmap',
    pin(PICKUP_PIN_COLOR, pickup),
    pin(DROP_PIN_COLOR, destination),
    `key=${encodeURIComponent(key)}`,
  ].join('&')}`;
};
